import React from 'react';
import { TableV3 } from '@/components/TableV3';
import { TerminalsIcon } from '@/assets';
import timeAgo from '@/ctUtlis/timeAgo';
import LoadingV2 from '@/components/Loading';

const ScheduleVesselList = ({
  vesselsList,
  activeTab,
  loading,
  tableTheadTitle = [],
}) => {
  const formatUpdatedAtDate = (dateString) => {
    function isInvalidDate(dateStr) {
      return isNaN(Date.parse(dateStr));
    }

    if (dateString === 'please contact your line') {
      return dateString;
    } else if (dateString === 'TBA') {
      return dateString;
    } else if (isInvalidDate(dateString) === true) {
      return '';
    } else {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${month}-${day}-${year} ${hours}:${minutes}`;
    }
  };

  function isDate(data) {
    if (typeof data === 'string' || typeof data === 'number') {
      const date = new Date(data);
      if (!isNaN(date.getTime())) {
        return typeof data === 'string' && !/^\d+$/.test(data);
      }
    }
    return false;
  }

  const getTerminalIcon = (terminalSlug) => {
    const iconMap = {
      APM: TerminalsIcon.APM,
      FMS: TerminalsIcon.FMS,
      EVERPORT: TerminalsIcon.EVERPORT,
      LACT: TerminalsIcon.LACT,
      PCT: TerminalsIcon.PCT,
      PST: TerminalsIcon.PST,
      TRAPAC: TerminalsIcon.TRAPAC,
      WBCT: TerminalsIcon.WBCT,
      WCC: TerminalsIcon.WCC,
      YTI: TerminalsIcon.YTI,
      TTI: TerminalsIcon.TTI,
      ITS: TerminalsIcon.ITS,
      LBCT: TerminalsIcon.LBCT,
      MATSON: TerminalsIcon.MATSON,
      'SSA MARINE': TerminalsIcon.SSA,
      'SSA STL': TerminalsIcon.STL,
      PTS: TerminalsIcon.PTS,
    };
    return iconMap[terminalSlug] || '';
  };

  const getFieldValue = (row, fieldName) => {
    if (activeTab === 0) {
      return row[`terminal_${fieldName}`] || row[`show_${fieldName}`];
    }
    return row[fieldName];
  };

  // Generate columns dynamically based on tableTheadTitle configuration
  const generateColumns = () => {
    const columnMap = {
      terminal: {
        sort: true,
        field: 'terminal',
        align: 'center',
        sortValue: (row) => {
          const terminalData = getFieldValue(row, 'terminal');
          const terminalSlug = terminalData?.slug;
          return terminalSlug;
        },
        component: (row) => {
          const terminalData = getFieldValue(row, 'terminal');
          const terminalSlug = terminalData?.slug;
          const terminalLinks = terminalData?.links?.vessel_schedule;

          return (
            <div className="py-1">
              <a
                target="_blank"
                rel="noreferrer"
                href={terminalLinks}
                className="flex items-center flex-col  hover:bg-border-light-secondary no-underline px-2 rounded-sm">
                <img
                  src={getTerminalIcon(terminalSlug)}
                  alt={terminalSlug}
                  className="max-h-20 object-contain max-w-64"
                />
                <span className="text-10 text-gray-400 m-0 whitespace-nowrap">
                  {terminalSlug}
                </span>
              </a>
            </div>
          );
        },
      },
      name: {
        sort: true,
        field: 'name',
        align: 'center',
        component: (row) => (
          <div className="whitespace-pre-wrap">
            {getFieldValue(row, 'name') || '-'}
          </div>
        ),
        sortValue: (row) => {
          return getFieldValue(row, 'name') || '-';
        },
      },
      voyage_in: {
        small: true,
        sort: true,
        field: 'voyage_in',
        align: 'center',
        component: (row) => getFieldValue(row, 'voyage_in') || '-',
        sortValue: (row) => {
          return getFieldValue(row, 'voyage_in') || '-';
        },
      },
      voyage_out: {
        small: true,
        sort: true,
        field: 'voyage_out',
        align: 'center',
        component: (row) => getFieldValue(row, 'voyage_out') || '-',
        sortValue: (row) => {
          return getFieldValue(row, 'voyage_out') || '-';
        },
      },
      ssl: {
        field: 'ssl',
        align: 'center',
        sort: true,
        sortValue: (row) => {
          const shippingLine = getFieldValue(row, 'shipping_line')?.slug;

          return shippingLine || '-';
        },
        component: (row) => {
          const shippingLine = getFieldValue(row, 'shipping_line');

          const sslLink = shippingLine?.links?.vessel;

          return sslLink ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={sslLink}
              className="hover:text-primary-blue text-gray-600 text-xs">
              {shippingLine?.slug}
            </a>
          ) : (
            shippingLine?.slug
          );
        },
      },
      eta: {
        sort: true,
        field: 'eta',
        align: 'center',
        component: (row) => {
          const etaValue = getFieldValue(row, 'eta');
          return (
            <div>
              {etaValue === null || etaValue === ''
                ? '-'
                : isDate(etaValue) !== true
                ? etaValue
                : formatUpdatedAtDate(`${etaValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'eta') || '-';
        },
      },
      ata: {
        sort: true,
        field: 'ata',
        align: 'center',
        component: (row) => {
          const ataValue = getFieldValue(row, 'ata');
          return (
            <div>
              {ataValue === null || ataValue === ''
                ? '-'
                : isDate(ataValue) !== true
                ? ataValue
                : formatUpdatedAtDate(`${ataValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'ata') || '-';
        },
      },
      etd: {
        sort: true,
        field: 'etd',
        align: 'center',
        component: (row) => {
          const etdValue = getFieldValue(row, 'etd');
          return (
            <div>
              {etdValue === null || etdValue === ''
                ? '-'
                : isDate(etdValue) !== true
                ? etdValue
                : formatUpdatedAtDate(`${etdValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'etd') || '-';
        },
      },
      atd: {
        sort: true,
        field: 'atd',
        align: 'center',
        component: (row) => {
          const atdValue = getFieldValue(row, 'atd');
          return (
            <div>
              {atdValue === null || atdValue === ''
                ? '-'
                : isDate(atdValue) !== true
                ? atdValue
                : formatUpdatedAtDate(`${atdValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'atd') || '-';
        },
      },
      epd: {
        sort: true,
        field: 'epd',
        align: 'center',
        component: (row) => {
          const epdValue = getFieldValue(row, 'epd');
          return (
            <div>
              {epdValue === null || epdValue === ''
                ? '-'
                : isDate(epdValue) !== true
                ? epdValue
                : formatUpdatedAtDate(`${epdValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'epd') || '-';
        },
      },
      erd_reefer: {
        sort: true,
        field: 'erd_reefer',
        align: 'center',
        component: (row) => {
          const erdReeferValue = getFieldValue(row, 'erd_reefer');
          return (
            <div>
              {erdReeferValue === null || erdReeferValue === ''
                ? '-'
                : isDate(erdReeferValue) !== true
                ? erdReeferValue
                : formatUpdatedAtDate(`${erdReeferValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'erd_reefer') || '-';
        },
      },
      erd_dry: {
        sort: true,
        field: 'erd_dry',
        align: 'center',
        component: (row) => {
          const erdDryValue = getFieldValue(row, 'erd_dry');
          return (
            <div>
              {erdDryValue === null || erdDryValue === ''
                ? '-'
                : isDate(erdDryValue) !== true
                ? erdDryValue
                : formatUpdatedAtDate(`${erdDryValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'erd_dry') || '-';
        },
      },
      cutoff_reefer: {
        sort: true,
        field: 'cutoff_reefer',
        align: 'center',
        component: (row) => {
          const cutoffReeferValue = getFieldValue(row, 'cutoff_reefer');
          return (
            <div
              className={`${
                isDate(cutoffReeferValue) !== true ? 'whitespace-pre-wrap' : ''
              }`}>
              {cutoffReeferValue === null || cutoffReeferValue === ''
                ? '-'
                : isDate(cutoffReeferValue) !== true
                ? cutoffReeferValue
                : formatUpdatedAtDate(`${cutoffReeferValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'cutoff_reefer') || '-';
        },
      },
      cutoff_hazard: {
        sort: true,
        field: 'cutoff_hazard',
        align: 'center',
        component: (row) => {
          const cutoffHazardValue = getFieldValue(row, 'cutoff_hazard');
          return (
            <div
              className={`${
                isDate(cutoffHazardValue) !== true ? 'whitespace-pre-wrap' : ''
              }`}>
              {cutoffHazardValue === null || cutoffHazardValue === ''
                ? '-'
                : isDate(cutoffHazardValue) !== true
                ? cutoffHazardValue
                : formatUpdatedAtDate(`${cutoffHazardValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'cutoff_hazard') || '-';
        },
      },
      cutoff_dry: {
        sort: true,
        field: 'cutoff_dry',
        align: 'center',
        component: (row) => {
          const cutoffDryValue = getFieldValue(row, 'cutoff_dry');
          return (
            <div
              className={`${
                isDate(cutoffDryValue) !== true ? 'whitespace-pre-wrap' : ''
              }`}>
              {cutoffDryValue === null || cutoffDryValue === ''
                ? '-'
                : isDate(cutoffDryValue) !== true
                ? cutoffDryValue
                : formatUpdatedAtDate(`${cutoffDryValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'cutoff_dry') || '-';
        },
      },
      elgd: {
        small: true,
        sort: true,
        field: 'elgd',
        align: 'center',
        component: (row) => {
          const elgdValue = getFieldValue(row, 'elgd');
          return (
            <div>
              {elgdValue === null || elgdValue === ''
                ? '-'
                : isDate(elgdValue) !== true
                ? elgdValue
                : formatUpdatedAtDate(`${elgdValue}`)}
            </div>
          );
        },
        sortValue: (row) => {
          return getFieldValue(row, 'elgd') || '-';
        },
      },
      updated_at: {
        sort: true,
        field: 'updated_at',
        align: 'center',
        component: (row) => {
          return (
            <div>
              {row.updated_at || row.terminal_updated_at || row.ssl_updated_at
                ? timeAgo(
                    `${
                      row.updated_at ||
                      row.terminal_updated_at ||
                      row.ssl_updated_at
                    }`
                  )
                : ''}
            </div>
          );
        },
        sortValue: (row) => {
          return (
            row.updated_at ||
            row.terminal_updated_at ||
            row.ssl_updated_at ||
            '-'
          );
        },
      },
    };

    // If no tableTheadTitle provided, use default columns
    if (!tableTheadTitle || tableTheadTitle.length === 0) {
      const defaultColumns = [
        {
          field: 'terminal',
          label: 'Terminal',
          align: 'center',
          flex: 4,
          sort: true,
          component: columnMap.terminal.component,
        },
        {
          field: 'name',
          label: 'Vessel',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.name.component,
          sortValue: columnMap.name.sortValue,
        },
        {
          field: 'voyage_in',
          label: 'Inbound Voyage',
          align: 'center',
          flex: 2,
          sort: true,
          small: true,
          component: columnMap.voyage_in.component,
        },
        {
          field: 'voyage_out',
          label: 'Outbound Voyage',
          align: 'center',
          flex: 2,
          sort: true,
          small: true,
          component: columnMap.voyage_out.component,
        },
        {
          field: 'ssl',
          label: 'SSL',
          align: 'center',
          flex: 3,
          sort: true,
          sortValue: columnMap?.ssl.sortValue,
          component: columnMap?.ssl.component,
        },
        {
          field: 'eta',
          label: 'ETA',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.eta.component,
          sortValue: columnMap.eta.sortValue,
        },
        {
          field: 'ata',
          label: 'ATA',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.ata.component,
          sortValue: columnMap.ata.sortValue,
        },
        {
          field: 'etd',
          label: 'ETD',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.etd.component,
          sortValue: columnMap.etd.sortValue,
        },
        {
          field: 'atd',
          label: 'ATD',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.atd.component,
          sortValue: columnMap.atd.sortValue,
        },
        {
          field: 'epd',
          label: 'Empty Pickup',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.epd.component,
          sortValue: columnMap.epd.sortValue,
        },
        {
          field: 'erd_reefer',
          label: 'ERD Refer',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.erd_reefer.component,
          sortValue: columnMap.erd_reefer.sortValue,
        },
        {
          field: 'erd_dry',
          label: 'ERD Dry',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.erd_dry.component,
          sortValue: columnMap.erd_dry.sortValue,
        },
        {
          field: 'cutoff_reefer',
          label: 'Cutoff Refer',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.cutoff_reefer.component,
          sortValue: columnMap.cutoff_reefer.sortValue,
        },
        {
          field: 'cutoff_hazard',
          label: 'Cutoff Hazard',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.cutoff_hazard.component,
          sortValue: columnMap.cutoff_hazard.sortValue,
        },
        {
          field: 'cutoff_dry',
          label: 'Cutoff Dry',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.cutoff_dry.component,
          sortValue: columnMap.cutoff_dry.sortValue,
        },
        {
          field: 'elgd',
          label: 'Extended Late Gate',
          align: 'center',
          flex: 3,
          sort: true,
          small: true,
          component: columnMap.elgd.component,
          sortValue: columnMap.elgd.sortValue,
        },
        {
          field: 'updated_at',
          label: 'Last Update',
          align: 'center',
          flex: 3,
          sort: true,
          component: columnMap.updated_at.component,
          sortValue: columnMap.updated_at.sortValue,
        },
      ];

      // When activeTab is 2, move SSL column to first position
      if (activeTab === 2) {
        const sslColumn = defaultColumns.find((col) => col.field === 'ssl');
        const otherColumns = defaultColumns.filter(
          (col) => col.field !== 'ssl'
        );
        return [sslColumn, ...otherColumns];
      }

      return defaultColumns;
    }

    // Generate columns based on tableTheadTitle configuration
    const generatedColumns = tableTheadTitle
      .filter((column) => column.show) // Only show columns that are enabled
      .map((column) => ({
        field: column.object,
        label: column.label,
        align: 'center',
        flex: column.flex,
        ...columnMap[column.object], // Spread the specific column configuration
      }));

    // When activeTab is 2, move SSL column to first position
    if (activeTab === 2) {
      const sslColumn = generatedColumns.find((col) => col.field === 'ssl');
      const otherColumns = generatedColumns.filter(
        (col) => col.field !== 'ssl'
      );
      return sslColumn ? [sslColumn, ...otherColumns] : generatedColumns;
    }

    return generatedColumns;
  };

  const columns = generateColumns();

  return (
    <div>
      {!(loading && vesselsList.length === 0) && (
        <div className="mb-4">
          <TableV3
            headers={columns}
            data={vesselsList || []}
            emptyMessage=""
            minWidth="1510px"
            stickyHeader={true}
            appHeaderHeight={64}
          />
        </div>
      )}

      {loading &&
        (vesselsList.length === 0 ||
          (vesselsList && vesselsList.length > 0)) && (
          <div className="flex justify-center py-4">
            <LoadingV2 />
          </div>
        )}
    </div>
  );
};

export default ScheduleVesselList;
